import { webMcpApi } from "./api/webMcpApi";
import type { ModelContext, ModelContextHost, WebMcpToolDefinition, WebMcpToolDescriptor } from "./types/webMcp";

const isLoggedIn = (): boolean => window.EASY.currentUser?.logged === true;

const webMcpAvailable = (): boolean => window.easySettings?.enable_web_mcp === true && isLoggedIn();

const isCapableContext = (context: ModelContext | undefined): context is ModelContext =>
  Boolean(context && (context.registerTool || context.provideContext));

// Prefers the first surface that actually exposes the API: a present-but-incapable
// document.modelContext must not mask a working legacy navigator.modelContext.
const findModelContext = (): ModelContext | undefined => {
  const candidates = [
    (document as unknown as ModelContextHost).modelContext,
    (navigator as unknown as ModelContextHost).modelContext,
  ];
  return candidates.find(isCapableContext);
};

const toolDescriptor = (tool: WebMcpToolDefinition): WebMcpToolDescriptor => ({
  ...tool,
  execute: (input, options) => webMcpApi.callTool(tool.name, input ?? {}, options?.signal),
});

// Resolves to the tool name when the registration was rejected; duplicate names or per-tool
// validation failures must not break the other tools.
async function registerSingleTool(
  context: ModelContext,
  descriptor: WebMcpToolDescriptor,
  signal: AbortSignal
): Promise<string | undefined> {
  try {
    await context.registerTool?.(descriptor, { signal });
    return undefined;
  } catch {
    return descriptor.name;
  }
}

async function registerTools(context: ModelContext, descriptors: WebMcpToolDescriptor[]): Promise<void> {
  const controller = new AbortController();
  // A persisted pagehide means the page enters the back/forward cache and can come back
  // with all its registrations, so the tools may only be unregistered on a real unload.
  window.addEventListener("pagehide", event => {
    if (!event.persisted) controller.abort();
  });

  const results = await Promise.all(
    descriptors.map(descriptor => registerSingleTool(context, descriptor, controller.signal))
  );
  const failed = results.filter((name): name is string => name !== undefined);
  if (failed.length > 0) console.warn(`WebMCP: ${failed.length} tool registration(s) failed`, failed);
}

async function exposeTools(context: ModelContext, descriptors: WebMcpToolDescriptor[]): Promise<void> {
  if (context.registerTool) {
    await registerTools(context, descriptors);
    return;
  }

  context.provideContext?.({ tools: descriptors });
}

export async function initWebMcp(): Promise<void> {
  if (!webMcpAvailable()) return;

  const context = findModelContext();
  if (!context) return;

  try {
    const tools = await webMcpApi.fetchToolDefinitions();
    if (tools.length === 0) return;

    await exposeTools(context, tools.map(toolDescriptor));
  } catch (error) {
    // WebMCP is best-effort; a failed fetch or registration must not break the page,
    // but it should stay diagnosable in the console.
    console.warn("WebMCP: initialization failed", error);
  }
}
